import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Users,
  Building2,
  Target,
  Hash,
  Calendar,
  Crown,
  BookOpen,
  MessageSquare,
  TrendingUp,
  Shuffle,
  Send,
  Sparkles,
  AlertCircle,
  CheckCircle,
  Share2,
  Clock,
  Link as LinkIcon,
  Briefcase,
  Camera,
  Code,
  GraduationCap,
  Palette
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { streamOpenAIResponse } from '../lib/openai';

interface QuizAnswers {
  platform: string;
  targetAudience: string[];
  topics: string[];
  postFrequency: string;
  growthStyle: string;
}

const FollowerCampaignQuiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({
    platform: '',
    targetAudience: [],
    topics: [],
    postFrequency: '',
    growthStyle: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<{
    weeklyPlan: string[];
    postIdeas: string[];
    hashtags: string[];
    bestTimes: string[];
    ctas: string[];
  } | null>(null);

  const steps = [
    {
      title: "What platform do you want to grow followers on?",
      description: "Choose your target social media platform",
      options: [
        { id: 'twitter', label: 'Twitter', icon: Users },
        { id: 'linkedin', label: 'LinkedIn', icon: Users },
        { id: 'instagram', label: 'Instagram', icon: Users },
        { id: 'facebook', label: 'Facebook', icon: Users },
        { id: 'youtube', label: 'YouTube', icon: Users }
      ]
    },
    {
      title: "Who is your ideal follower?",
      description: "Select your target audience",
      options: [
        { id: 'entrepreneurs', label: 'Entrepreneurs & Founders', icon: Briefcase },
        { id: 'marketers', label: 'Marketers & Growth Hackers', icon: TrendingUp },
        { id: 'creators', label: 'Content Creators', icon: Camera },
        { id: 'developers', label: 'Developers & Engineers', icon: Code },
        { id: 'students', label: 'Students & Learners', icon: GraduationCap },
        { id: 'professionals', label: 'Business Professionals', icon: Building2 },
        { id: 'coaches', label: 'Coaches & Consultants', icon: Target },
        { id: 'designers', label: 'Designers & Artists', icon: Palette }
      ],
      multiSelect: true
    },
    {
      title: "What do you post about?",
      description: "Choose 1-3 main topics",
      options: [
        { id: 'ai', label: 'AI & Technology' },
        { id: 'business', label: 'Business' },
        { id: 'productivity', label: 'Productivity' },
        { id: 'lifestyle', label: 'Lifestyle' },
        { id: 'education', label: 'Education' },
        { id: 'marketing', label: 'Marketing' },
        { id: 'finance', label: 'Finance' },
        { id: 'health', label: 'Health & Wellness' }
      ],
      multiSelect: true
    },
    {
      title: "How often do you want to post?",
      description: "Select your ideal posting frequency",
      options: [
        { id: '3x', label: '3x per week', icon: Calendar },
        { id: '5x', label: '5x per week', icon: Calendar },
        { id: 'daily', label: 'Daily', icon: Calendar }
      ]
    },
    {
      title: "How do you want to grow?",
      description: "Choose your primary growth strategy",
      options: [
        { id: 'authority', label: 'Authority Content', icon: Crown },
        { id: 'personal', label: 'Personal Stories', icon: MessageSquare },
        { id: 'educational', label: 'Educational Content', icon: BookOpen },
        { id: 'viral', label: 'Viral/Trending', icon: TrendingUp },
        { id: 'mixed', label: 'Mixed Approach', icon: Shuffle }
      ]
    }
  ];

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Save campaign data
      const { data: campaign, error: campaignError } = await supabase
        .from('follower_campaigns')
        .insert({
          user_id: user.id,
          platform: answers.platform,
          target_audience: answers.targetAudience,
          topics: answers.topics,
          post_frequency: answers.postFrequency,
          growth_style: answers.growthStyle
        })
        .select()
        .single();

      if (campaignError) throw campaignError;

      // Generate content using OpenAI
      const prompt = `Create a follower growth campaign for ${answers.platform}. 
Target audience: ${answers.targetAudience}
Topics: ${answers.topics.join(', ')}
Posting frequency: ${answers.postFrequency}
Growth style: ${answers.growthStyle}

Format your response as JSON with the following structure:
{
  "weeklyPlan": ["day 1 strategy", "day 2 strategy", "day 3 strategy"],
  "postIdeas": ["post idea 1", "post idea 2", "post idea 3"],
  "hashtags": ["hashtag1", "hashtag2", "hashtag3"],
  "bestTimes": ["time 1", "time 2", "time 3"],
  "ctas": ["cta 1", "cta 2", "cta 3"]
}`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            const data = JSON.parse(responseText);
            setGeneratedContent(data);
          } catch (e) {
            // Partial response, continue collecting
          }
        },
        (error) => {
          throw error;
        }
      );

      // Save generated content
      if (generatedContent) {
        const contentPromises = generatedContent.postIdeas.map((content, index) => 
          supabase
            .from('follower_campaign_content')
            .insert({
              campaign_id: campaign.id,
              content,
              post_type: answers.growthStyle,
              hashtags: generatedContent.hashtags,
              best_time: generatedContent.bestTimes[index % generatedContent.bestTimes.length],
              cta: generatedContent.ctas[index % generatedContent.ctas.length]
            })
        );

        await Promise.all(contentPromises);
      }

    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {step.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {step.description}
          </p>
        </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {step.options.map((option) => {
              const Icon = option.icon || Users;
              const field = 
                currentStep === 0 ? 'platform' :
                currentStep === 1 ? 'targetAudience' :
                currentStep === 2 ? 'topics' :
                currentStep === 3 ? 'postFrequency' : 'growthStyle';
              
              const isSelected = step.multiSelect
                ? answers[field] instanceof Array && answers[field].includes(option.id)
                : answers[field] === option.id;

              return (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setAnswers(prev => ({
                    ...prev,
                    [field]: step.multiSelect
                      ? (prev[field] instanceof Array
                          ? isSelected
                            ? prev[field].filter(id => id !== option.id)
                            : [...prev[field], option.id]
                          : [option.id])
                      : option.id
                  }))}
                  className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    isSelected
                      ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                      : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="ml-3 font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </span>
                  {isSelected && (
                    <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  )}
                </motion.button>
              );
            })}
          </div>
      </div>
    );
  };

  const renderResults = () => {
    if (!generatedContent) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Your Follower Growth Campaign
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Here's your personalized strategy to grow your {answers.platform} following
          </p>
        </div>

        {/* Weekly Content Plan */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
              Weekly Content Plan
            </h3>
          </div>
          <div className="space-y-4">
            {generatedContent.weeklyPlan.map((strategy, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800/50 rounded-lg p-4"
              >
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                    Day {index + 1}
                  </span>
                </div>
                <p className="text-gray-700 dark:text-gray-300">{strategy}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Post Ideas */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Follower Magnet Posts
          </h3>
          {generatedContent.postIdeas.map((idea, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-white">{idea}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {generatedContent.hashtags.slice(0, 3).map((hashtag, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-gray-100 dark:bg-dark-700 rounded-full text-sm text-gray-600 dark:text-gray-400"
                      >
                        #{hashtag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center space-x-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Best time: {generatedContent.bestTimes[index % generatedContent.bestTimes.length]}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      CTA: {generatedContent.ctas[index % generatedContent.ctas.length]}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Growth Tips */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center space-x-3 mb-4">
            <Target className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Engagement Hack</h3>
          </div>
          <p className="text-purple-100">
            Comment on 10 posts per day from accounts in your niche. Focus on adding value
            and building genuine connections rather than self-promotion.
          </p>
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={() => {
              setGeneratedContent(null);
              setCurrentStep(0);
              setAnswers({
                platform: '',
                targetAudience: [],
                topics: [],
                postFrequency: '',
                growthStyle: ''
              });
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Start Over
          </button>
          <div className="space-x-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate More
            </button>
            <button
              onClick={() => navigate('/calendar')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule All
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Generating your campaign...</p>
        </div>
      ) : generatedContent ? (
        renderResults()
      ) : (
        <div className="space-y-8">
          {/* Progress bar */}
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-400">
                  Progress
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {Math.round((currentStep / (steps.length - 1)) * 100)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200 dark:bg-indigo-900/40">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600"
              />
            </div>
          </div>

          {renderCurrentStep()}

          <div className="flex justify-between pt-6">
            {currentStep > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/campaign-generator')}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={
                currentStep === 0 ? !answers.platform :
                currentStep === 1 ? answers.targetAudience.length === 0 :
                currentStep === 2 ? answers.topics.length === 0 :
                currentStep === 3 ? !answers.postFrequency :
                !answers.growthStyle
              }
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                (currentStep === 0 && answers.platform) ||
                (currentStep === 1 && answers.targetAudience.length > 0) ||
                (currentStep === 2 && answers.topics.length > 0) ||
                (currentStep === 3 && answers.postFrequency) ||
                (currentStep === 4 && answers.growthStyle)
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Generate
                  <Send className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowerCampaignQuiz;